import { Marca } from "@notes/ui";
import { Mic, ListChecks, BarChart3, MessageSquareWarning, UserRound, Plug, ArrowRight, ShieldCheck } from "lucide-react";
import { Onda } from "./Onda.js";

/**
 * A PÁGINA PÚBLICA DE PLANOS.
 *
 * É para onde o botão "Notes" do CRM manda quem ainda não tem o produto, então
 * ela é a primeira coisa que a agência vê. Ela vive FORA da sessão: quem chega
 * aqui não tem conta, e cair no login seria a porta que não abre.
 *
 * A TABELA está em `PLANOS`, num lugar só. Mudar preço é editar esta constante,
 * e não caçar número espalhado por texto de marketing.
 *
 * Por que assento e hora, e não mensalidade por agência como no CRM: o custo do
 * Notes é por CONVERSA (transcrição e modelo), então cobrar por agência faria a
 * agência que grava tudo pagar o mesmo que a que grava uma reunião por semana.
 * A unidade da conta é a mesma que o produto já mede (`assentos ×
 * horas_por_assento`), então o que a tela do plano mostra é o que a fatura diz.
 */

/** Desconto do anual: a MESMA régua do CRM (Essencial, Performance e Escala ~20%). */
const DESCONTO_ANUAL = 0.2;

const PLANOS = [
  {
    chave: "time",
    nome: "Time",
    // O rótulo do botão vem junto do plano: "Começar com o Operação" é o tipo
    // de concordância que só quebra depois que a página está no ar.
    chamada: "Começar com o Time",
    resumo: "Para a equipe que quer parar de perder o que foi combinado na conversa.",
    mensalPorAssento: 189,
    horasPorAssento: 15,
    inclui: [
      "Gravação por extensão do navegador e áudio do celular",
      "Resumo, tarefas e combinados de cada conversa",
      "Nota da conversa com o trecho que sustenta",
      "Promessa que fecha sozinha quando a prova chega do CRM",
      "Aba de reuniões na negociação e cartão no contato",
      "Verificação em duas etapas e controle das sessões abertas",
    ],
  },
  {
    chave: "operacao",
    nome: "Operação",
    chamada: "Começar com a Operação",
    destaque: true,
    resumo: "Para quem treina o time e precisa ver a agência inteira, não uma conversa.",
    mensalPorAssento: 289,
    horasPorAssento: 30,
    inclui: [
      "Tudo do Time, com o dobro de horas por assento",
      "Painel da equipe: lacunas, ranking e voz do cliente",
      "Coach por conversa, com elogio junto da correção",
      "Playbook da sua agência no lugar do padrão da casa",
      "Perguntar ao acervo inteiro por sentido",
      "Chaves de API, webhooks assinados e conector de IA",
    ],
  },
] as const;

/** Hora além do incluído. Cobre o custo de transcrição e modelo com folga. */
const HORA_EXTRA = 9;
/** Abaixo disto o Notes não se paga para a agência nem para a casa. */
const ASSENTOS_MINIMO = 3;

const reais = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
export function Planos() {
  // O destino do contato é o site da casa, e não um número escrito aqui:
  // telefone inventado numa página pública é o tipo de erro que só aparece
  // quando um cliente tenta falar com a gente e não consegue.
  const origem = new URLSearchParams(window.location.search).get("origem");
  const contato = `https://entur.com.br/?assunto=enturos-notes${origem === "crm" ? "&origem=crm" : ""}`;

  return (
    <div className="enotes enotes-planos">
      <header className="enotes-planos-topo">
        <Marca tamanho={24} />
        <a className="enotes-botao" data-variante="fantasma" data-tamanho="sm" href="/entrar">Já tenho acesso</a>
      </header>

      <section className="enotes-planos-capa">
        <div className="enotes-planos-arte" aria-hidden><Onda /></div>
        <div className="enotes-planos-texto">
          <p className="eyebrow">EnturOS Notes</p>
          <h1 className="enotes-planos-titulo">
            Toda conversa com o cliente diz mais do que cabe na memória,
            <em> e o Notes escuta por você.</em>
          </h1>
          <p className="enotes-planos-sub">
            Reunião, ligação e áudio viram resumo, tarefa, compromisso e registro na negociação do
            EnturOS CRM. O que ficou prometido não se perde, e ninguém digita nada.
          </p>
          <div className="enotes-planos-acoes">
            <a className="enotes-botao" data-variante="primario" data-tamanho="lg" href="#planos">
              Ver os planos <ArrowRight size={15} />
            </a>
            <a className="enotes-botao" data-variante="contorno" data-tamanho="lg" href={contato} target="_blank" rel="noreferrer">
              Falar com a Entur
            </a>
          </div>
        </div>
      </section>

      <section className="enotes-planos-precos" id="planos">
        <div className="enotes-planos-precos-topo">
          <p className="eyebrow">Planos</p>
          <h2 className="enotes-planos-h2">Preço por assento, com horas de conversa incluídas</h2>
          <p className="enotes-planos-sub">
            Assento é quem tem conversa gravada, e não quem abre o sistema. As horas do ciclo são somadas
            pela agência inteira: quem gravou menos cobre quem gravou mais. Mínimo de {ASSENTOS_MINIMO} assentos.
          </p>
        </div>

        <div className="enotes-planos-cartoes">
          {PLANOS.map((p) => {
            const anualPorMes = Math.round(p.mensalPorAssento * (1 - DESCONTO_ANUAL));
            return (
              <article key={p.chave} className="enotes-plano" data-destaque={"destaque" in p && p.destaque ? "sim" : undefined}>
                {"destaque" in p && p.destaque && <span className="enotes-plano-selo">o mais escolhido</span>}
                <h3>{p.nome}</h3>
                <p className="enotes-plano-resumo">{p.resumo}</p>
                <p className="enotes-plano-preco">
                  <span className="num">{reais(p.mensalPorAssento)}</span>
                  <span className="enotes-plano-unidade">por assento, por mês</span>
                </p>
                <p className="enotes-plano-anual">
                  {reais(anualPorMes)} por assento no plano anual, cobrado uma vez por ano
                </p>
                <p className="enotes-plano-horas">
                  <strong className="num">{p.horasPorAssento} h</strong> de conversa por assento a cada mês
                </p>
                <ul className="enotes-plano-lista">
                  {p.inclui.map((i) => <li key={i}>{i}</li>)}
                </ul>
                <a className="enotes-botao" data-variante={"destaque" in p && p.destaque ? "primario" : "contorno"}
                  href={contato} target="_blank" rel="noreferrer">
                  {p.chamada}
                </a>
              </article>
            );
          })}
        </div>

        <p className="enotes-planos-rodape-preco">
          Hora além do incluído: {reais(HORA_EXTRA)}, cobrada só se acontecer. Rede e franquia têm proposta
          própria: <a className="enotes-linha-link" href={contato} target="_blank" rel="noreferrer">fale com a Entur</a>.
        </p>
      </section>

      <section className="enotes-planos-grade">
        {[
          { icone: Mic, titulo: "Escuta sem robô na sala", texto: "A extensão do navegador grava a reunião on-line, e o aviso aparece para todo mundo. Áudio do celular também entra." },
          { icone: BarChart3, titulo: "Nota da conversa com evidência", texto: "Cada dimensão do seu playbook vem com o trecho que a sustenta. Avaliação sem trecho é opinião." },
          { icone: ListChecks, titulo: "Promessa que se cobra sozinha", texto: "O que o consultor prometeu vira pendência, e fecha quando a prova chega do CRM: proposta enviada, tarefa concluída." },
          { icone: MessageSquareWarning, titulo: "Objeção e concorrente à vista", texto: "O que o cliente levantou e ninguém tratou aparece no painel da equipe, com quantas vezes e quando." },
          { icone: UserRound, titulo: "Memória do viajante", texto: "Preferência dita na conversa vira ficha do contato: voo direto, hotel com estrutura para criança, o que for." },
          { icone: Plug, titulo: "Dentro do EnturOS CRM", texto: "Aba de reuniões na negociação, cartão no contato, e um clique entre os dois produtos sem novo login." },
        ].map((c) => (
          <article key={c.titulo} className="enotes-planos-cartao">
            <span className="enotes-planos-icone" aria-hidden><c.icone size={17} /></span>
            <h2>{c.titulo}</h2>
            <p>{c.texto}</p>
          </article>
        ))}
      </section>

      <section className="enotes-planos-garantia">
        <span className="enotes-planos-icone" aria-hidden><ShieldCheck size={17} /></span>
        <div>
          <h2>A inteligência propõe, a pessoa aprova</h2>
          <p>
            Nada é escrito no CRM sem alguém conferir a evidência e aprovar. A gravação fica no Brasil,
            cada agência enxerga só o que é dela, e a conversa do cliente nunca vira comando para a máquina.
          </p>
        </div>
      </section>

      <footer className="enotes-planos-rodape carimbo">EnturOS · Notes</footer>
    </div>
  );
}
